import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CIRCLE_SECTION_LABELS, CIRCLE_SECTIONS, STARTER_DETAILS, type CircleSection } from '../../shared/sections'
import { Choices } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { api, type Me } from '@/lib/api'
import type { Family } from '@/lib/families'

// Grow this circle: what it has, on Circle settings. A circle starts from what it was made for (a friends circle
// with photos and moments, a group with photos and recipes) and anyone in it can turn on more, or turn something off
// again. Off only hides it here. Sections the owner has switched off for everyone aren't offered.
export function GrowCircle({ family, me }: { family: Family; me: Me }) {
  const queryClient = useQueryClient()
  const key = ['family', family.id]
  const save = useMutation({
    mutationFn: (sections: Partial<Record<CircleSection, boolean>>) =>
      api(`/families/${family.id}/sections`, { method: 'PUT', body: sections }),
    // Each change shows at once; whatever the server then says is what stays.
    onMutate: (sections) =>
      queryClient.setQueryData<Family>(key, (f) => f && { ...f, sections: { ...f.sections, ...sections } }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: ['circles'] }),
      ]),
  })
  const offered = CIRCLE_SECTIONS.filter((s) => me.sections[s])
  if (offered.length === 0) return null
  const started = family.starter ? STARTER_DETAILS[family.starter].label : null
  return (
    <section className="flex flex-col gap-4 border-t pt-8" aria-labelledby="grow">
      <div className="flex flex-col gap-1.5">
        <h2 id="grow" className="text-title">
          Grow this circle
        </h2>
        {started && <p className="text-sub text-muted-foreground">Started as {started}.</p>}
      </div>
      <Choices<CircleSection>
        legend="What this circle has"
        hint="Turning something off hides it in this circle for everyone here. Nothing is deleted, and Keep a copy still includes it."
        value={offered.filter((s) => family.sections[s])}
        options={offered.map((s) => [s, CIRCLE_SECTION_LABELS[s]])}
        onChange={(next) => {
          const changed = offered.filter((s) => next.includes(s) !== family.sections[s])
          if (changed.length) save.mutate(Object.fromEntries(changed.map((s) => [s, next.includes(s)])))
        }}
      />
      <FormError error={save.error} />
    </section>
  )
}
